<p-toast key="feedback-toast" position="bottom-left" appendTo="body" [life]="8000" styleClass="feedback-toast-container">
  <ng-template pTemplate="message" let-message>
    <div class="feedback-toast">
      <i class="pi pi-check-circle feedback-toast__icon"></i>
      <div class="feedback-toast__body">
        <span class="feedback-toast__title">Created Ticket {{ message.summary }}</span>
        <div class="feedback-toast__actions">
          <a (click)="openTicket(message.data.url)">View in Jira</a>
          <span>·</span>
          <a (click)="viewInTable(message.data.source)">View in table</a>
          <span>·</span>
          <a (click)="copyUrl(message.data.url)">{{ urlCopied ? 'Copied!' : 'Copy link' }}</a>
        </div>
      </div>
    </div>
  </ng-template>
</p-toast>

<p-dialog
  [(visible)]="visible"
  [modal]="true"
  [style]="{ width: '52vw' }"
  [breakpoints]="{ '960px': '80vw', '640px': '95vw' }"
  [draggable]="false"
  [resizable]="false"
  (onHide)="close()"
>
  <ng-template pTemplate="header">
    <span class="p-dialog-title">
      <i class="pi pi-flag" style="margin-right: 8px;"></i>
      Report an Issue
    </span>
  </ng-template>

  <div *ngIf="existingTickets.length > 0" class="existing-tickets-warning">
    <i class="pi pi-exclamation-triangle"></i>
    <div class="existing-tickets-warning__body">
      <strong>Existing ticket{{ existingTickets.length > 1 ? 's' : '' }} for this entity:</strong>
      <span *ngFor="let t of existingTickets; let last = last">
        <a (click)="openTicket(t.url)">{{ t.key }}</a> — {{ t.summary }}<span *ngIf="!last">, </span>
      </span>
    </div>
  </div>

  <form [formGroup]="form" class="report-issue-form">

    <!-- Summary -->
    <div class="p-field">
      <label for="summary">Summary <span class="required">*</span></label>
      <input
        id="summary"
        type="text"
        pInputText
        formControlName="summary"
        placeholder="Brief description of the issue"
        class="w-full"
      />
    </div>

    <!-- Description -->
    <div class="p-field">
      <label for="description">Description <span class="required">*</span></label>
      <textarea
        id="description"
        pInputTextarea
        formControlName="description"
        placeholder="What happened?"
        rows="3"
        class="w-full"
      ></textarea>
    </div>

    <!-- Steps to Reproduce -->
    <div class="p-field">
      <label for="stepsToReproduce">Steps to Reproduce</label>
      <textarea
        id="stepsToReproduce"
        pInputTextarea
        formControlName="stepsToReproduce"
        placeholder="1. Go to...&#10;2. Click on...&#10;3. Observe..."
        rows="3"
        class="w-full"
      ></textarea>
    </div>

    <!-- Expected / Actual -->
    <div class="two-col">
      <div class="p-field">
        <label for="expectedBehavior">Expected Behavior</label>
        <textarea
          id="expectedBehavior"
          pInputTextarea
          formControlName="expectedBehavior"
          placeholder="What should happen?"
          rows="2"
          class="w-full"
        ></textarea>
      </div>
      <div class="p-field">
        <label for="actualBehavior">Actual Behavior</label>
        <textarea
          id="actualBehavior"
          pInputTextarea
          formControlName="actualBehavior"
          placeholder="What actually happens?"
          rows="2"
          class="w-full"
        ></textarea>
      </div>
    </div>

    <!-- Source / Team + Type row -->
    <div class="two-col">
      <div class="p-field">
        <label for="source">Source / Team <span class="required">*</span></label>
        <p-dropdown
          id="source"
          formControlName="source"
          [options]="sourceOptions"
          placeholder="Select team"
          styleClass="w-full"
        ></p-dropdown>
      </div>
      <div class="p-field">
        <label for="issueType">Type <span class="required">*</span></label>
        <p-dropdown
          id="issueType"
          formControlName="issueType"
          [options]="issueTypeOptions"
          placeholder="Select type"
          styleClass="w-full"
        ></p-dropdown>
      </div>
    </div>

    <!-- Severity -->
    <div class="p-field">
      <label for="severity">Severity</label>
      <p-dropdown
        id="severity"
        formControlName="severity"
        [options]="severityOptions"
        placeholder="Select severity"
        styleClass="w-full"
      ></p-dropdown>
    </div>

    <!-- Attachments -->
    <div class="p-field">
      <label>Attachments <span class="optional">(optional)</span></label>
      <p-fileUpload
        #fileUpload
        mode="basic"
        accept="image/*,video/*"
        [multiple]="true"
        [maxFileSize]="524288000"
        chooseLabel="Attach Files"
        [auto]="false"
        (onSelect)="onFileSelect($event)"
        styleClass="p-button-outlined p-button-sm"
      ></p-fileUpload>
      <div *ngIf="attachmentFiles.length > 0" class="files-selected">
        <div *ngFor="let file of attachmentFiles; let i = index" class="file-selected">
          <i class="pi pi-check-circle"></i>
          <span>{{ file.name }}</span>
          <button
            pButton pRipple
            type="button"
            icon="pi pi-times"
            class="p-button-text p-button-rounded p-button-sm p-button-danger"
            pTooltip="Remove attachment"
            (click)="removeAttachment(i)"
          ></button>
        </div>
      </div>
    </div>

    <!-- Auto-populated context -->
    <div class="context-info">
      <small>
        <span *ngIf="entityType">
          <strong>Entity:</strong> {{ entityType }}<span *ngIf="entityId"> #{{ entityId }}</span>
          &nbsp;|&nbsp;
        </span>
        <span *ngIf="resolvedEnvironment"><strong>Env:</strong> {{ resolvedEnvironment }} &nbsp;|&nbsp;</span>
        <strong>Reporter:</strong> {{ reporterName || reporterEmail }}
        &nbsp;|&nbsp;
        <strong>URL:</strong> <span class="page-url">{{ pageUrl }}</span>
      </small>
    </div>

  </form>

  <ng-template pTemplate="footer">
    <button
      pButton pRipple
      label="Cancel"
      icon="pi pi-times"
      class="p-button-text p-button-sm"
      (click)="close()"
      [disabled]="submitting"
    ></button>
    <button
      pButton pRipple
      label="Submit Ticket"
      icon="pi pi-send"
      class="p-button-sm"
      (click)="onSubmit()"
      [disabled]="form.invalid || submitting"
      [loading]="submitting"
    ></button>
  </ng-template>
</p-dialog>
